/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
/** @jsx jsx */

import React, { Fragment } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { useIntl } from 'react-intl'
import { css, jsx } from '@emotion/react'

import type { EuiTabbedContentTab } from '@elastic/eui'
import { EuiBetaBadge, EuiSpacer, useEuiTheme } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import SupplyProjectsMetadata from '@modules/project-admin-components/SupplyProjectsMetadata'
import AdminProjectList from '@modules/project-admin-components/ProjectList'
import SuspendProjectsButton from '@modules/project-admin-components/SuspendProjectsButton'
import OrganizationOverviewCCMClusterTable from '@modules/ccm-admin-components/OrganizationOverviewCCMClusterTable'
import {
  organizationBillingHistoryUrl,
  organizationBillingCreateBudgetUrl,
  organizationBillingEditBudgetUrl,
  organizationBillingBudgetsAndNotificationsUrl,
  organizationBillingOverviewUrl,
  organizationConnectedClustersUrl,
  organizationDeploymentsUrl,
  organizationMembersUrl,
  organizationOverviewUrl,
  organizationProjectsUrl,
  organizationSecurityUrl,
  organizationUsageUrl,
} from '@modules/urls/organizations'
import type { OrganizationTab } from '@modules/cloud-lib/types/pages'

import BillingOverview from '@/components/Billing/BillingOverview'
import BillingUsage from '@/components/Billing/BillingUsage'
import BillingHistory from '@/components/Billing/BillingHistory'
import BillingNotifications from '@/components/Billing/BillingNotifications'
import CreateBudget from '@/components/Billing/BillingNotifications/Budgets/CreateBudget'
import EditBudget from '@/components/Billing/BillingNotifications/Budgets/EditBudget'

import OrganizationOverview from '../OrganizationOverview'
import OrganizationDeployments from '../OrganizationDeployments'
import OrganizationMembers from '../OrganizationMembers'
import Security from '../../Security'

import type { AllProps } from '../types'

const nonTabbedContentBasedOnUrl = ({
  organizationId,
  params: { budgetId },
  pathname,
  tabId,
}: {
  organizationId: string
  params: { budgetId?: string }
  pathname: string
  tabId: OrganizationTab
}): React.FunctionComponent | undefined => {
  switch (tabId) {
    case 'billing-budgets': {
      if (pathname === organizationBillingCreateBudgetUrl(organizationId)) {
        return CreateBudget
      }

      if (budgetId && pathname === organizationBillingEditBudgetUrl(organizationId, budgetId)) {
        return EditBudget
      }

      return
    }

    default:
      return
  }
}

type Props = Pick<
  AllProps,
  | 'ccmFeature'
  | 'fetchOrganizationInvitationsRequest'
  | 'fetchOrganizationMembersRequest'
  | 'organizationMemberRows'
  | 'saasOrganization'
  | 'searchResults'
  | 'selectedTabId'
  | 'serverlessFeature'
>

const useOrganizationContent = ({
  ccmFeature,
  fetchOrganizationInvitationsRequest,
  fetchOrganizationMembersRequest,
  organizationMemberRows,
  saasOrganization,
  searchResults,
  selectedTabId,
  serverlessFeature,
}: Props): {
  tabs: EuiTabbedContentTab[]
  selectedTab: EuiTabbedContentTab
  NonTabbedContent: React.FunctionComponent | undefined
} => {
  const { pathname } = useLocation()
  const params = useParams<{ budgetId?: string }>()
  const { euiTheme } = useEuiTheme()
  const { formatMessage } = useIntl()

  let NonTabbedContent: React.FunctionComponent | undefined

  if (!saasOrganization) {
    return {
      tabs: [],
      selectedTab: {} as EuiTabbedContentTab,
      NonTabbedContent: undefined,
    }
  }

  // organization data
  const {
    organization: { organization_id: organizationId, organization_name: organizationName },
  } = saasOrganization
  const organization = { id: organizationId, name: organizationName }

  // organization requests data
  const { error: fetchInvitationsError, inProgress: isFetchingInvitations } =
    fetchOrganizationInvitationsRequest
  const { error: fetchMembersError, inProgress: isFetchingMembers } =
    fetchOrganizationMembersRequest
  const isLoading = isFetchingInvitations || isFetchingMembers
  const organizationError = fetchInvitationsError || fetchMembersError

  // deployments data
  const deploymentCount = searchResults ? searchResults.match_count : undefined

  const tabs: Array<Omit<EuiTabbedContentTab, 'id'> & { id: OrganizationTab; route: string }> = [
    {
      id: 'overview',
      name: (
        <FormattedMessage id='organizations.organization.overview-tab' defaultMessage='Overview' />
      ),
      route: organizationOverviewUrl(organizationId),
      content: (
        <Fragment>
          <EuiSpacer size='xxl' />

          <OrganizationOverview saasOrganization={saasOrganization} />
        </Fragment>
      ),
    },
    {
      id: 'members',
      name: (
        <FormattedMessage id='organizations.organization.members-tab' defaultMessage='Members' />
      ),
      route: organizationMembersUrl(organizationId),
      content: (
        <Fragment>
          <EuiSpacer size='xxl' />
          <OrganizationMembers
            organizationId={organizationId}
            organization={organization}
            organizationMemberRows={organizationMemberRows}
            isLoading={isLoading}
            error={organizationError}
          />
        </Fragment>
      ),
    },
    {
      id: 'deployments',
      name: (
        <Fragment>
          <FormattedMessage
            id='organizations.organization.deployments-tab'
            defaultMessage='Deployments'
          />

          {deploymentCount !== undefined && <span> ({deploymentCount})</span>}
        </Fragment>
      ),
      route: organizationDeploymentsUrl(organizationId),
      content: (
        <Fragment>
          <EuiSpacer size='xxl' />

          <OrganizationDeployments organizationId={organizationId} />
        </Fragment>
      ),
    },
    ...(serverlessFeature
      ? [
          {
            id: 'projects' as OrganizationTab,
            name: (
              <SupplyProjectsMetadata organizationId={organizationId}>
                {({ projectsLength, isReady }) => (
                  <Fragment>
                    <FormattedMessage
                      id='organizations.organization.projects-tab'
                      defaultMessage='Projects'
                    />
                    {isReady && <span> ({projectsLength})</span>}
                  </Fragment>
                )}
              </SupplyProjectsMetadata>
            ),
            route: organizationProjectsUrl(organizationId),
            content: (
              <Fragment>
                <EuiSpacer size='xxl' />
                <AdminProjectList
                  organizationId={organizationId}
                  extraActions={<SuspendProjectsButton organizationId={organizationId} />}
                />
              </Fragment>
            ),
          },
        ]
      : []),
    ...(ccmFeature
      ? [
          {
            id: 'connected-clusters' as OrganizationTab,
            name: (
              <FormattedMessage
                id='organizations.organization.connected-clusters-tab'
                defaultMessage='Connected clusters'
              />
            ),
            route: organizationConnectedClustersUrl(organizationId),
            content: (
              <Fragment>
                <EuiSpacer size='xxl' />
                <OrganizationOverviewCCMClusterTable organizationId={organizationId} />
              </Fragment>
            ),
          },
        ]
      : []),
    {
      id: 'billing-overview',
      name: (
        <FormattedMessage
          id='organizations.organization.billing-overview-tab'
          defaultMessage='Billing overview'
        />
      ),
      route: organizationBillingOverviewUrl(organizationId),
      content: (
        <Fragment>
          <EuiSpacer size='xxl' />

          <BillingOverview />
        </Fragment>
      ),
    },
    {
      id: 'usage',
      name: <FormattedMessage id='organizations.organization.usage-tab' defaultMessage='Usage' />,
      route: organizationUsageUrl(organizationId),
      content: (
        <Fragment>
          <EuiSpacer size='xxl' />

          <BillingUsage />
        </Fragment>
      ),
    },
    {
      id: 'billing-history',
      name: (
        <FormattedMessage
          id='organizations.organization.billing-history-tab'
          defaultMessage='Billing history'
        />
      ),
      route: organizationBillingHistoryUrl(organizationId),
      content: (
        <Fragment>
          <EuiSpacer size='xxl' />

          <BillingHistory />
        </Fragment>
      ),
    },
    {
      id: 'billing-budgets' as OrganizationTab,
      'data-test-subj': 'billing-and-notifications-tab',
      name: (
        <FormattedMessage
          id='organizations.organization.billing-budgets-tab'
          defaultMessage='Budgets and notifications'
        />
      ),
      route: organizationBillingBudgetsAndNotificationsUrl(organizationId),
      content: (
        <Fragment>
          <EuiSpacer size='xxl' />

          <BillingNotifications />
        </Fragment>
      ),
      append: (
        <EuiBetaBadge
          color='accent'
          size='s'
          css={css({ backgroundColor: euiTheme.colors.primary })}
          label={formatMessage({ id: 'billing-tabs.budgets.new', defaultMessage: 'NEW' })}
        />
      ),
    },
    {
      id: 'security',
      name: (
        <FormattedMessage id='organizations.organization.security-tab' defaultMessage='Security' />
      ),
      route: organizationSecurityUrl(organizationId),
      content: (
        <Fragment>
          <EuiSpacer size='xxl' />

          <Security organizationId={organizationId} readonly={true} />
        </Fragment>
      ),
    },
  ]

  if (selectedTabId) {
    NonTabbedContent = nonTabbedContentBasedOnUrl({
      organizationId,
      params,
      pathname,
      tabId: selectedTabId,
    })
  }

  const selectedTab =
    tabs.find((tab) => tab.id === selectedTabId) || (tabs[0] as EuiTabbedContentTab)

  return {
    tabs,
    selectedTab,
    NonTabbedContent,
  }
}

export default useOrganizationContent
